/**
 * MIRRORED FILE — client/src/utils/clientVisibleFields.ts <-> src/utils/clientVisibleFields.js
 * Change one, change both. `src/__tests__/mirroredUtilParity.test.js` is the
 * list and the enforcement; a twin that is not in it is an orphan the moment it
 * is written.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE CLIENT SEES — one owner, because there were two answers.
 * ---------------------------------------------------------------------------
 *
 * ClickUp 86d478wad. "The client view modal should only show the things we
 * actually export for the client." It did not, and the reason is the shape this
 * codebase fails in most often: the same rule written down twice.
 *
 *   - This component's side: `ClientViewPopup.tsx` kept a hand-maintained
 *     column set and its own summary rows.
 *   - The real rule: `src/services/quotationExport.service.js`, in the two
 *     dedicated client generators (`generateQuotationClientExcel`,
 *     `generateQuotationClientPDF`) and the template path's `isClientView`
 *     branches.
 *
 * They drifted, and the drift ran the dangerous way — the PREVIEW showed more
 * than the export sends. The modal is the screen with the SEND button on it, so
 * two things a client never receives were being shown as though they would be:
 *
 *   1. a **Profit** row — USC's own margin, in a window captioned CLIENT VIEW;
 *   2. a **More Details** column (`item.moreDetails`), which no export writes
 *      at all, client or admin.
 *
 * Both were removed from the preview rather than added to the export, at the
 * owner's direction (2026-09-02).
 *
 * Registered in docs/BUSINESS_RULES.md.
 */

export interface ClientVisibleItemField {
  key: string;
  /** The full caption an export writes. */
  label: string;
  /** For the narrow preview grid, where "Quantity" does not fit a 70px column. */
  shortLabel: string;
}

export interface ClientVisibleSummaryRow {
  key: string;
  label: string;
}

/** The client-visible LINE fields, in the order a client-facing view prints them. */
export const CLIENT_VISIBLE_ITEM_FIELDS: ReadonlyArray<ClientVisibleItemField> = Object.freeze([
  Object.freeze({ key: 'lineNumber', label: '#', shortLabel: '#' }),
  Object.freeze({ key: 'itemName', label: 'Item Name', shortLabel: 'Item Name' }),
  Object.freeze({ key: 'quantity', label: 'Quantity', shortLabel: 'Qty' }),
  Object.freeze({ key: 'uom', label: 'Unit', shortLabel: 'Unit' }),
  Object.freeze({ key: 'unitSellingPrice', label: 'Unit Selling Price', shortLabel: 'Unit Price' }),
  Object.freeze({ key: 'totalSellingPrice', label: 'Total Selling Price', shortLabel: 'Total' }),
  Object.freeze({ key: 'remarks', label: 'Remarks', shortLabel: 'Remarks' }),
]);

/**
 * The client-visible SUMMARY rows, in order.
 *
 * Profit is deliberately absent: neither client generator emits it, and this
 * popup showing it was the reported bug. Discount stays — the client is told
 * the discount they were given.
 */
export const CLIENT_VISIBLE_SUMMARY_ROWS: ReadonlyArray<ClientVisibleSummaryRow> = Object.freeze([
  Object.freeze({ key: 'totalPrice', label: 'Total Price' }),
  Object.freeze({ key: 'discount', label: 'Discount' }),
  Object.freeze({ key: 'finalPrice', label: 'Final Price' }),
]);

/**
 * Line fields that must never reach a client, spelled as the Excel template
 * mapper names them. A DENY list on purpose — see the server twin for why the
 * template path cannot use an allow list.
 */
export const INTERNAL_ONLY_ITEM_FIELDS: ReadonlyArray<string> = Object.freeze([
  'supplierItemNo',
  'internalRemarks',
  'buyerRemark', // D103.13
  'supplier',
  'competitorPrice',
]);

/**
 * Column HEADINGS that mark a column internal, matched as lowercase substrings.
 *
 * The template path cannot use the deny list above — a customer-supplied .xlsx
 * carries whatever captions the customer wrote — so internal columns are also
 * recognised by what the sheet calls them. See the server twin for the full
 * account: TECH_DEBT #213, where this vocabulary existed twice inside
 * `quotationExport.service.js` and the two copies disagreed. This is their
 * union, so both paths hide a superset of what either hid before.
 *
 * Nothing on the client reads this yet; it lives here because the twins are
 * compared as wholes and a rule that exists on one side only is the drift the
 * mirror is for.
 */
export const INTERNAL_ONLY_COLUMN_KEYWORDS: ReadonlyArray<string> = Object.freeze([
  'markup',
  'cost',
  'internal',
  'supplier item',
  'competitor',
  'profit',
  'margin',
  'pricing source',
  'vendor',
]);

/** The ordered client-visible line fields. A copy — callers must not mutate the rule. */
export function clientVisibleItemFields(): ClientVisibleItemField[] {
  return CLIENT_VISIBLE_ITEM_FIELDS.slice();
}

/** The ordered client-visible summary rows. A copy, for the same reason. */
export function clientVisibleSummaryRows(): ClientVisibleSummaryRow[] {
  return CLIENT_VISIBLE_SUMMARY_ROWS.slice();
}

/** Does this line field belong in a client-facing view? */
export function isClientVisibleItemField(field: string): boolean {
  return CLIENT_VISIBLE_ITEM_FIELDS.some((f) => f.key === field);
}

/** Is this line field one that must never cross to a client? */
export function isInternalOnlyItemField(field: string): boolean {
  return INTERNAL_ONLY_ITEM_FIELDS.indexOf(field) !== -1;
}

/** Does this summary row belong in a client-facing view? */
export function isClientVisibleSummaryRow(row: string): boolean {
  return CLIENT_VISIBLE_SUMMARY_ROWS.some((r) => r.key === row);
}

/**
 * Does this column heading mark the column internal?
 *
 * Takes the raw cell text; casing and surrounding whitespace are the sheet's
 * business, not the caller's. A blank heading is not internal — an unlabelled
 * column is far more likely to be a spacer than a margin.
 */
export function isInternalOnlyColumnHeading(heading: unknown): boolean {
  const text = String(heading == null ? '' : heading).toLowerCase().trim();
  if (!text) return false;
  return INTERNAL_ONLY_COLUMN_KEYWORDS.some((k) => text.indexOf(k) !== -1);
}
